import { defineComponent as _defineComponent } from 'vue'
import { billingApi } from '@/api';
import AmexSvg from '@/assets/payments/AmexSvg.vue';
import ApplePaySvg from '@/assets/payments/ApplePaySvg.vue';
import BacsDebitSvg from '@/assets/payments/BacsDebitSvg.vue';
import CardGenericSvg from '@/assets/payments/CardGenericSvg.vue';
import DinersSvg from '@/assets/payments/DinersSvg.vue';
import DiscoverSvg from '@/assets/payments/DiscoverSvg.vue';
import GooglePaySvg from '@/assets/payments/GooglePaySvg.vue';
import JcbSvg from '@/assets/payments/JcbSvg.vue';
import MastercardSvg from '@/assets/payments/MastercardSvg.vue';
import VisaSvg from '@/assets/payments/VisaSvg.vue';
import Button from '@/components/buttons/Button.vue';
import ButtonMenu from '@/components/buttons/ButtonMenu.vue';
import Chip from '@/components/interface/Chip.vue';
import NoData from '@/components/placeholders/NoData.vue';
import Table from '@/components/table/Table.vue';
import i18n from '@/i18n';
import { paymentMethodHasExpired } from '@/lib/billing/billingFunctions';
import { Icon } from '@/lib/enum/Icon';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { showSuccess } from '@/plugins/ToastNotifications';
import { useRouteReactive, useUpdateRouteQuery } from '@/router/composition';
import store from '@/store';
import { MenuItemType } from '@/types/propTypes';
import { viewport } from '@/util/windowFunctions';
import ListSettingsWrapper from '@/views/settings/common/ListSettingsWrapper';
import { Component, computed, onMounted, ref } from 'vue';
import {
  BillingPaymentMethod,
  BillingPaymentMethodTypeEnum,
  BillingPaymentMethodWalletTypeEnum,
} from '../../../../api/v1';


export default /*#__PURE__*/_defineComponent({
  __name: 'ManagePaymentMethods',
  props: {
    allowAdding: { type: Boolean, default: true }
  },
  setup(__props: any) {



const paymentMethods = realtimeQuery(Entity.BillingPaymentMethod).fetch();
const modifyingPaymentMethods = ref<boolean>(false);
const route = useRouteReactive();
const updateRouteQuery = useUpdateRouteQuery();

const setupPaymentMethod = async () => {
  await billingApi
    .createPaymentMethodSetupSession({
      createPaymentMethodSetupSessionData: {
        successPath: route.value.fullPath,
        cancelPath: route.value.fullPath,
      },
    })
    .then(({ data }) => {
      window.location.href = data.redirectUrl;
    });
};

const addPaymentMethod = async () => {
  modifyingPaymentMethods.value = true;
  await billingApi
    .createPaymentMethod({
      createPaymentMethodData: {
        stripeCheckoutSessionId: String(route.value.query.sid),
      },
    })
    .then(async () => {
      await updateRouteQuery({ sid: undefined });
    })
    .finally(() => {
      modifyingPaymentMethods.value = false;
    });
};

const deletePaymentMethod = async (paymentMethod: BillingPaymentMethod) => {
  modifyingPaymentMethods.value = true;
  await billingApi
    .deletePaymentMethod({ id: paymentMethod.id })
    .then(() => {
      showSuccess(
        i18n.tc('successMessage.genericDeleted', 1, {
          entity: i18n.tc('entities.paymentMethod', 1).toLowerCase(),
        }),
      );
    })
    .finally(() => {
      modifyingPaymentMethods.value = false;
    });
};

const makeDefaultPaymentMethod = async (
  paymentMethod: BillingPaymentMethod,
) => {
  modifyingPaymentMethods.value = true;
  await billingApi
    .updatePaymentMethod({
      id: paymentMethod.id,
      updatePaymentMethodData: { isDefault: true },
    })
    .finally(() => {
      modifyingPaymentMethods.value = false;
    });
};

const timezone = computed<string>(() => store.getters.timezone);

const menuItems = (paymentMethod: BillingPaymentMethod): MenuItemType[] => {
  return [
    {
      id: 1,
      value: i18n.tc('button.makeDefault'),
      icon: Icon.Star,
      action: async () => {
        await makeDefaultPaymentMethod(paymentMethod);
      },
      hide:
        paymentMethod.isDefault ||
        paymentMethodHasExpired(paymentMethod, timezone.value),
    },
    {
      id: 2,
      value: i18n.tc('button.delete'),
      icon: `${Icon.TrashCan} !text-red`,
      hide: paymentMethod.isDefault,
      action: async () => {
        await deletePaymentMethod(paymentMethod);
      },
    },
  ].filter((i) => !i.hide);
};

const paymentMethodIcon = (paymentMethod: BillingPaymentMethod): Component => {
  if (paymentMethod.type === BillingPaymentMethodTypeEnum.Card) {
    const cardImages = {
      visa: VisaSvg,
      mastercard: MastercardSvg,
      amex: AmexSvg,
      diners: DinersSvg,
      discover: DiscoverSvg,
      jcb: JcbSvg,
    };

    const walletImages = {
      [BillingPaymentMethodWalletTypeEnum.ApplePay]: ApplePaySvg,
      [BillingPaymentMethodWalletTypeEnum.GooglePay]: GooglePaySvg,
    };

    if (
      paymentMethod.walletType &&
      paymentMethod.walletType.toLowerCase() in walletImages
    ) {
      return walletImages[paymentMethod.walletType.toLowerCase()];
    }

    return cardImages[paymentMethod.cardBrand.toLowerCase()] || CardGenericSvg;
  }

  if (paymentMethod.type === BillingPaymentMethodTypeEnum.BacsDebit) {
    return BacsDebitSvg;
  }

  return CardGenericSvg;
};

onMounted(async () => {
  if (route.value.query.sid !== undefined) {
    await addPaymentMethod();
  }
});

return { __sfc: true,paymentMethods, modifyingPaymentMethods, route, updateRouteQuery, setupPaymentMethod, addPaymentMethod, deletePaymentMethod, makeDefaultPaymentMethod, timezone, menuItems, paymentMethodIcon, Button, ButtonMenu, Chip, NoData, Table, paymentMethodHasExpired, Icon, viewport, ListSettingsWrapper }
}

})